import React, { useCallback, useState } from "react";

import Dropzone from "react-dropzone";
import { FieldProps } from "formik";
import { ImageListView } from "@brushfire/types";
import Button from "../../../Button";
import Icon from "../../../Icon";
import Image from "../../../Image";
import { Modal } from "../../../../Layout";

// import Error from "../Error";
// import Input from "../Input";

export interface IUploaderProps {
	field?: FieldProps["field"];
	form?: FieldProps["form"];
	id: string;
	name: string;
	label: string;
	className?: string;
	accept?: string | string[];
	allowMultiple: boolean;
	uploadCallback?: (file?: ImageListView) => void;
	chooseExistingCallback?: (file?: ImageListView) => void;
	children: React.ReactNode;
	files: ImageListView[];
}

const Uploader: React.FC<IUploaderProps> = ({
	className,
	accept,
	field,
	form,
	id,
	label,
	allowMultiple = false,
	uploadCallback,
	chooseExistingCallback,
	children,
	files,
	...props
}) => {
	const [showPreview, setShowPreview] = useState(false);
	const [showFilePicker, setShowFilePicker] = useState(false);

	const onDrop = useCallback((acceptedFiles: any) => {
		//TODO add multiple file handling here
		acceptedFiles.forEach((file: any) => {
			if (form && field) {
				const reader = new FileReader();

				reader.onabort = () => console.log("file reading was aborted");
				reader.onerror = () => console.log("file reading has failed");
				reader.onload = () => {
					const binaryStr = reader.result + "|" + file.name;
					form.setFieldValue(field.name, binaryStr);
					if (uploadCallback) {
						uploadCallback({ uri: binaryStr, name: file.name });
					}
				};
				reader.readAsDataURL(file);
			}
		});
	}, []);

	const onDropRejected = useCallback((rejectedFiles: any) => {
		if (form && field) {
			const errors: string[] = rejectedFiles.map((file: any) => {
				return rejectedFiles.name;
			});
			form.setFieldError(
				field.name,
				`${errors.join(", ")} ${errors.length == 1 ? "is" : "are"} not the correct file format`
			);
		}
	}, []);

	const chooseExisting = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setShowFilePicker(true);
	};

	const chooseExistingFile = (file: ImageListView) => {
		if (form && field) {
			form.setFieldValue(field.name, file.uri);
			if (chooseExistingCallback) {
				chooseExistingCallback(file);
			}
		}
		setShowFilePicker(false);
	};

	const previewImage = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setShowPreview(true);
	};

	const removeImage = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (form && field) {
			form.setFieldValue(field.name, undefined);
			if (chooseExistingCallback) {
				chooseExistingCallback(undefined);
			}
		}
	};

	return (
		<>
			<Dropzone
				multiple={allowMultiple}
				onDrop={onDrop}
				accept={accept ? accept : ["image/jpeg", "image/jpg", "image/gif", "image/png"]}
				onDropRejected={onDropRejected}
				{...field}
			>
				{({ getRootProps, getInputProps, isDragActive, isDragAccept }) => (
					<div
						{...getRootProps()}
						className={`form-uploader ${className ? className : ""} ${
							isDragActive ? (isDragAccept ? "active-drag valid" : "active-drag invalid") : ""
						}`}
					>
						<input {...getInputProps()} />
						{field && !field.value && children}
						<div className="form-options">
							{field && !!field.value && (
								<Button
									purpose="primary"
									classes="form-uploader-new form-uploader-btn btn-sm"
									onClick={previewImage}
									title="Image Preview"
									type="button"
								>
									<Icon icon="eye" />
								</Button>
							)}
							<Button
								purpose="primary"
								classes="form-uploader-new form-uploader-btn btn-sm"
								title="Upload A New File"
								type="button"
							>
								Upload New
							</Button>
							{files && files.length > 0 && (
								<Button
									purpose="primary"
									classes="form-uploader-existing form-uploader-btn btn-sm"
									onClick={chooseExisting}
									title="Choose An Existing File"
									type="button"
								>
									Choose Existing
								</Button>
							)}

							{field && !!field.value && (
								<Button
									purpose="danger"
									classes="form-uploader-new form-uploader-btn btn-sm"
									onClick={removeImage}
									title="Remove Image"
									type="button"
								>
									Remove
								</Button>
							)}
						</div>
						{field && !!field.value && (
							<div
								className="form-uploader-value"
								style={{ backgroundImage: `url("${field.value.split("|")[0]}")` }}
							></div>
						)}
						<div className="form-uploader-coverall blur-bg"></div>
					</div>
				)}
			</Dropzone>
			{field && !!field.value && (
				<Modal show={showPreview} setShow={setShowPreview} title="Image Preview">
					<img
						className="img-responsive"
						src={field.value.split("|")[0]}
						alt={`File uploaded for ${label}`}
					/>
				</Modal>
			)}
			{showFilePicker && (
				<Modal show={showFilePicker} setShow={setShowFilePicker} title="Existing Image Picker">
					{files ? (
						<div className="file-picker brushfire-2019">
							{files.map((file) => (
								<Button
									key={file.name}
									onClick={() => {
										chooseExistingFile(file);
									}}
									purpose="info"
									classes="choice btn-block no-border"
								>
									<div
										className="file-image-preview"
										style={{ backgroundImage: `url(${file.uri})` }}
									></div>
									<div className="file-name">{file.name}</div>
								</Button>
							))}
						</div>
					) : (
						<div>Loading...</div>
					)}
				</Modal>
			)}
		</>
	);
};

export default Uploader;
